import React from 'react';
import CustomScrollbar from '../../TestPage/components/CustomScrollbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Flex from '../../../components/base/Flex';
import { useTheme } from '@emotion/react';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { format } from 'date-fns';

const ConsoleLogs = ({ consoleLogs }) => {
  const theme = useTheme();

  // Function to parse log level and style accordingly
  const getLogLevel = (log) => {
    if (typeof log !== 'string') return null;

    if (log.includes('[ERROR]')) return 'error';
    if (log.includes('[WARN]')) return 'warning';
    if (log.includes('[INFO]')) return 'info';
    if (log.includes('[DEBUG]')) return 'debug';
    return 'log';
  };

  const getLogColor = (level) => {
    switch (level) {
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
        return theme.palette.info.main;
      case 'debug':
        return theme.palette.success.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden'
      }}>
      <Box
        sx={{
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
          Console Logs
          <Chip
            label={`${consoleLogs?.length || 0} entries`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Typography>
      </Box>

      <CustomScrollbar
        sx={{
          flex: 1,
          maxHeight: '80vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          bgcolor: theme.palette.background.default
        }}>
        {!!consoleLogs?.length ? (
          consoleLogs.map((log, i) => {
            const level = getLogLevel(log);
            const timestamp = format(new Date(), 'HH:mm:ss.SSS');

            return (
              <React.Fragment key={i}>
                <Box
                  component={'div'}
                  sx={{
                    px: 2,
                    py: 1.5,
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    bgcolor: i % 2 === 0 ? 'transparent' : theme.palette.action.hover
                  }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      minWidth: 80,
                      flexShrink: 0
                    }}>
                    {timestamp}
                  </Typography>
                  {level && (
                    <Chip
                      label={level.toUpperCase()}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: getLogColor(level),
                        color: theme.palette.getContrastText(getLogColor(level)),
                        flexShrink: 0
                      }}
                    />
                  )}
                  <Typography
                    component="pre"
                    sx={{
                      m: 0,
                      color: level ? getLogColor(level) : theme.palette.text.primary,
                      // overflowX: 'auto',
                      flexGrow: 1,
                      whiteSpace: 'pre-wrap',
                      maxWidth: 'calc(100% - 100px)',
                      wordBreak: 'break-word'
                    }}>
                    {log}
                  </Typography>
                </Box>
                {i < consoleLogs.length - 1 && <Divider sx={{ mx: 2 }} />}
              </React.Fragment>
            );
          })
        ) : (
          <Flex
            columnGap={3}
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexDirection: 'column',
              p: 4
            }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                bgcolor: theme.palette.action.hover,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}>
              <Typography variant="h4" color="text.secondary">
                📋
              </Typography>
            </Box>
            <Typography variant="body1" textAlign="center" color="text.secondary">
              Console logs from the device will appear here
            </Typography>
            <Typography variant="caption" textAlign="center" color="text.disabled" sx={{ mt: 1 }}>
              No logs available yet
            </Typography>
          </Flex>
        )}
      </CustomScrollbar>
    </Box>
  );
};

export default ConsoleLogs;
